import React, { useEffect, useState, Fragment } from "react";
import { usePersistentState } from "../../components/Common/usePersistentFilters";
import { Row, Col, Card, Button, Modal, Form, Badge, Spinner } from "react-bootstrap";
import AppPagination from "../../components/Common/AppPagination";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const ProductMaster = () => {

 const [list, setList] = useState([]);
 const [categories, setCategories] = useState([]);

 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);

 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);

 const [search, setSearch] = usePersistentState("product_master_search", "");
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });

 const [form, setForm] = useState({
 productName: "",
 serialNo: "",
 modelNo: "",
 category: "",
 description: "",
 status: "Active",
 });

 /* =========================
 FETCH PRODUCTS
 ========================= */

 const fetchProducts = async (page = 1) => {
 setLoading(true);

 try {
 const res = await api.get(`/products?includeInactive=true&page=${page}&limit=${pagination.limit}&search=${search}`);

 if (res.data.success) {
 setList(res.data.data);
 setPagination(prev => ({
 ...prev,
 ...res.data.pagination,
 currentPage: res.data.pagination.currentPage
 }));
 }

 } finally {
 setLoading(false);
 }
 };

 /* =========================
 FETCH CATEGORIES
 ========================= */

 const fetchCategories = async () => {
 const res = await api.get("/product-categories");

 if (res.data.success) {
 setCategories(res.data.data);
 }
 };

 useEffect(() => {
 fetchProducts(1);
 fetchCategories();
 }, [search]);

 const handlePageChange = (page) => {
 fetchProducts(page);
 };

 /* =========================
 MODAL
 ========================= */

 const openAdd = () => {

 setEditId(null);

 setForm({
 productName: "",
 serialNo: "",
 modelNo: "",
 category: "",
 description: "",
 status: "Active",
 });

 setShowModal(true);
 };

 const openEdit = (item) => {

 setEditId(item._id);

 setForm({
 productName: item.productName,
 serialNo: item.serialNo || "",
 modelNo: item.modelNo,
 category: item.category?._id || "",
 description: item.description || "",
 status: item.status,
 });

 setShowModal(true);
 };

 const closeModal = () => {
 if (!saving) setShowModal(false);
 };

 const handleChange = (e) =>
 setForm({ ...form, [e.target.name]: e.target.value });

 /* =========================
 SAVE PRODUCT
 ========================= */

 const handleSave = async () => {

 if (!form.productName.trim())
 return swal("Validation", "Product name required", "warning");

 if (!form.modelNo.trim())
 return swal("Validation", "Model number required", "warning");

 try {

 setSaving(true);

 if (editId) {
 await api.put(`/products/${editId}`, form);
 } else {
 await api.post("/products", form);
 }

 swal("Success", "Product saved successfully", "success");

 closeModal();

 fetchProducts();

 } catch (err) {

 swal(
 "Error",
 err.response?.data?.message || "Operation failed",
 "error"
 );

 } finally {
 setSaving(false);
 }
 };

 /* =========================
 DEACTIVATE
 ========================= */

 const deactivate = (item) => {

 swal({
 title: `Deactivate "${item.productName}"?`,
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {

 if (!ok) return;

 try {

 await api.delete(`/products/${item._id}`);

 fetchProducts();

 } catch (err) {

 swal(
 "Error",
 err.response?.data?.message || "Cannot deactivate",
 "error"
 );
 }
 });
 };

 return (
 <Fragment>

 <PageTitle activeMenu="Product Master" motherMenu="Masters" />

 <Row>
 <Col lg={12}>
 <Card>

 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Products</Card.Title>
 <Button onClick={openAdd}>+ Add Product</Button>
 </Card.Header>

 <Card.Body>

 {/* SEARCH */}

 <div className="mb-3">
 <Form.Control
 placeholder="Search product or model..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </div>

 {/* LOADING */}

 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (

 <table className="table table-bordered table-hover">

 <thead className="table-light">
 <tr>
 <th>Serial No</th>
 <th>Product Name</th>
 <th>Model No</th>
 <th>Category</th>
 <th>Status</th>
 <th width="170">Actions</th>
 </tr>
 </thead>

 <tbody>

 {list.map((item, index) => (
 <tr key={item._id}>

 <td>{item.serialNo || "-"}</td>

 <td>{item.productName}</td>

 <td>{item.modelNo}</td>

 <td>{item.category?.name || "-"}</td>

 <td>
 <Badge
 bg={item.status === "Active" ? "success" : "secondary"}
 >
 {item.status}
 </Badge>
 </td>

 <td>

 <Button
 size="sm"
 variant="warning"
 className="me-2"
 onClick={() => openEdit(item)}
 >
 Edit
 </Button>

 {item.status === "Active" && (
 <Button
 size="sm"
 variant="danger"
 onClick={() => deactivate(item)}
 >
 Deactivate
 </Button>
 )}

 </td>

 </tr>
 ))}

 </tbody>

 </table>
 )}

 {!loading && list.length === 0 && (
 <div className="text-center text-muted py-4">
 No products found
 </div>
 )}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={list.length}
 limit={pagination.limit}
 onPageChange={handlePageChange}
 />
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= MODAL ================= */}

 <Modal show={showModal} onHide={closeModal} backdrop={saving ? "static" : true}>

 <Modal.Header closeButton={!saving}>
 <Modal.Title>
 {editId ? "Edit Product" : "Add Product"}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>

 <Form.Group className="mb-3">
 <Form.Label>Serial No</Form.Label>
 <Form.Control
 name="serialNo"
 value={form.serialNo}
 onChange={handleChange}
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Product Name *</Form.Label>
 <Form.Control
 name="productName"
 value={form.productName}
 onChange={handleChange}
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Model No *</Form.Label>
 <Form.Control
 name="modelNo"
 value={form.modelNo}
 onChange={handleChange}
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Category</Form.Label>
 <Form.Select
 name="category"
 value={form.category}
 onChange={handleChange}
 disabled={saving}
 >

 <option value="">Select Category</option>

 {categories.map((c) => (
 <option key={c._id} value={c._id}>
 {c.name}
 </option>
 ))}

 </Form.Select>
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Description</Form.Label>
 <Form.Control
 name="description"
 as="textarea"
 rows={2}
 value={form.description}
 onChange={handleChange}
 disabled={saving}
 />
 </Form.Group>

 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 name="status"
 value={form.status}
 onChange={handleChange}
 disabled={saving}
 >
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Form.Group>

 </Modal.Body>

 <Modal.Footer>

 <Button variant="secondary" onClick={closeModal} disabled={saving}>
 Cancel
 </Button>

 <Button onClick={handleSave} disabled={saving}>
 {saving ? "Saving..." : "Save"}
 </Button>

 </Modal.Footer>

 </Modal>

 </Fragment>
 );
};

export default ProductMaster;